import { Connection, Field } from '@lucidtech/las-sdk-browser';
import { Key, memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Pressable, SelectStateContext } from 'react-aria-components';

import {
  Badge,
  Button,
  Flex,
  Heading,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue,
  Text,
} from '@/components/core';
import { AddIcon, CloseIcon } from '@/components/icons';
import { useGetAgentFromPath } from '@/hooks';
import { toModelId, useGetModel } from '@/hooks/api';
import { useTriggerStore } from '@/store';
import { findModelId } from '@/utils';

import { GraphObject } from '../client';
import { useGraphClient } from '../hooks';

const ClearSelection = () => {
  const state = useContext(SelectStateContext);

  return (
    <Pressable onPress={() => state?.setSelectedKey(null)}>
      <CloseIcon className="size-[0.8rem] !text-gray-400" />
    </Pressable>
  );
};

type FieldMap = Record<string, string> | undefined;

const IFieldMapper = ({ connection }: { connection?: Connection }) => {
  const siteId = useTriggerStore((state) => state.action.config.siteId) as string | undefined;
  const driveId = useTriggerStore((state) => state.action.config.driveId) as string | undefined;
  const driveItemId = useTriggerStore((state) => state.action.config.driveItemId) as string | undefined;
  const tableId = useTriggerStore((state) => state.action.config.tableId) as string | undefined;
  const updateAction = useTriggerStore((state) => state.updateAction);
  const fieldToColumnMap = useTriggerStore((state) => state.action.config?.fieldToColumnMap) as FieldMap;

  const graphClient = useGraphClient(connection);
  const [columns, setColumns] = useState<GraphObject[]>([]);

  const { data: agent } = useGetAgentFromPath();
  const { data: model } = useGetModel(toModelId(findModelId(agent)));

  const fields: [string, Field][] = useMemo(() => {
    if (!model?.fieldConfig) {
      return [];
    }

    const result: [string, Field][] = [];
    for (const [fieldId, field] of Object.entries(model.fieldConfig)) {
      if (field.type !== 'table') {
        result.push([fieldId, field]);
      } else {
        if (field.fields) {
          const subfields: [string, Field][] = Object.entries(field.fields).map(([id, subfield]) => [id, subfield]);
          result.push(...subfields);
        }
      }
    }

    return result;
  }, [model?.fieldConfig]);

  useEffect(() => {
    if (driveItemId && tableId && siteId && driveId) {
      graphClient.getTableColumns(siteId, driveId, driveItemId, tableId).then((result) => {
        setColumns(result);
      });
    } else {
      setColumns([]);
    }
  }, [driveId, driveItemId, graphClient, siteId, tableId]);

  const onSelectionChange = useCallback(
    (fieldId: Key, column: GraphObject) => {
      if (column.id) {
        const newFieldMapping: typeof fieldToColumnMap = {};
        for (const key in fieldToColumnMap) {
          if (fieldToColumnMap[key] !== column.id) {
            newFieldMapping[key] = fieldToColumnMap[key];
          }
        }

        if (fieldId) {
          newFieldMapping[fieldId.toString()] = column.id;
        }

        updateAction((state) => ({
          config: { ...state.config, fieldToColumnMap: newFieldMapping },
        }));
      }
    },
    [fieldToColumnMap, updateAction]
  );

  const findFieldIdFromColumn = useCallback(
    (g?: GraphObject) =>
      fieldToColumnMap
        ? Object.keys(fieldToColumnMap).find((fieldId) => fieldToColumnMap?.[fieldId] === g?.id)
        : undefined,
    [fieldToColumnMap]
  );

  return (
    <>
      {columns?.length ? <Heading size="sm">Map fields</Heading> : null}
      <Flex col className="gap-sm">
        {columns?.map((column) => (
          <Select
            className="pl-md group/field-mapping w-full border-l-2 border-gray-200"
            onSelectionChange={(key) => onSelectionChange(key, column)}
            defaultSelectedKey={column.id ? findFieldIdFromColumn(column) : undefined}
          >
            <Flex col className="gap-xs">
              <Label>{column.name}</Label>

              <Flex className="gap-xs items-center">
                <Button variant="secondary">
                  <SelectValue>
                    {({ defaultChildren, isPlaceholder }) =>
                      isPlaceholder ? (
                        <Flex variant="plain" className="group/placeholder gap-xs">
                          <AddIcon className="!text-gray-500" />
                          <Text variant="secondary">Choose field</Text>
                        </Flex>
                      ) : (
                        <Badge variant="plain" className="gap-xs">
                          <Text>{defaultChildren}</Text>
                          <ClearSelection />
                        </Badge>
                      )
                    }
                  </SelectValue>
                </Button>
              </Flex>
            </Flex>
            <Popover>
              <ListBox>
                {fields.map(([fieldId, field]) => {
                  return (
                    <ListBoxItem id={fieldId} key={fieldId}>
                      <Text>{field.name}</Text>
                    </ListBoxItem>
                  );
                })}
              </ListBox>
            </Popover>
          </Select>
        ))}
      </Flex>
    </>
  );
};

export const FieldMapper = memo(IFieldMapper) as typeof IFieldMapper;
